import { Button, buttonColors, buttonSizes, buttonTypes } from "../.."
import { CloseIcon, StarIcon } from "../../../assets/icons"
import cx from "classnames"

import type { UsePwaInstallOptions } from "./usePwaInstall"
import type { PwaInstallSheetLiterals } from "./pwaInstallSheet"

import styles from "./index.module.css"
import { usePwaInstall } from "./usePwaInstall"
import { PwaInstallSheet } from "./pwaInstallSheet"

export type PwaInstallLiterals = PwaInstallSheetLiterals & {
  notificationText: string
  buttonText: string
}

export type PwaInstallProps = {
  appName: string
  appUrl: string
  appIcon: string
  appLogo?: string
  literals: PwaInstallLiterals
  showRating?: boolean
  ratingCount?: number
} & Pick<UsePwaInstallOptions, "isVisible" | "onDismiss" | "deferredPrompt" | "platform" | "storageKey" | "showAlways" | "environment">

export const PwaInstall = ({
  appName,
  appUrl,
  appIcon,
  appLogo,
  literals,
  showRating = true,
  ratingCount = 5,
  isVisible = true,
  onDismiss,
  deferredPrompt,
  platform,
  storageKey = "showPWAInfo",
  showAlways = false,
  environment,
}: PwaInstallProps) => {
  const pwa = usePwaInstall({
    isVisible,
    onDismiss,
    deferredPrompt: deferredPrompt ?? null,
    platform,
    storageKey,
    showAlways,
    environment,
  })

  const shouldRender =
    pwa.shouldRenderNotification || pwa.shouldRenderInstructions || pwa.showNotification || pwa.showInstructions
  if (!shouldRender && !isVisible) return null
  if (pwa.isInstalled) return null

  return (
    <>
      {pwa.shouldRenderNotification && (
        <div className={cx(styles.notification, pwa.showNotification && styles.notificationVisible)}>
          <div className={styles.notificationContent}>
            <button className={styles.closeButton} onClick={pwa.dismiss} aria-label="Close notification" type="button">
              <CloseIcon />
            </button>
            {appIcon && (
              <div className={styles.icon}>
                <img src={appIcon} alt={appName} className={styles.iconImage} />
              </div>
            )}
            <div className={styles.notificationTextBlock}>
              <p className={styles.notificationTitle}>{literals.notificationText}</p>
              {showRating && ratingCount > 0 && (
                <div className={styles.ratingRow} aria-label={`Rating ${ratingCount} out of 5`}>
                  {Array.from({ length: ratingCount }).map((_, idx) => (
                    <span key={idx} className={styles.ratingIcon}>
                      <StarIcon />
                    </span>
                  ))}
                </div>
              )}
            </div>
            <Button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                pwa.openInstructions()
              }}
              type={buttonTypes.OUTLINE}
              color={buttonColors.PRIMARY}
              size={buttonSizes.SMALL}
              className={styles.installButton}
            >
              {literals.buttonText}
            </Button>
          </div>
        </div>
      )}

      <PwaInstallSheet
        pwa={pwa}
        literals={literals}
        appName={appName}
        appUrl={appUrl}
        appLogo={appLogo}
      />
    </>
  )
}

export { usePwaInstall } from "./usePwaInstall"
export { PwaInstallSheet } from "./pwaInstallSheet"
export { PwaInstallCard } from "./pwaInstallCard"
export type { PwaInstallSheetLiterals, PwaInstallSheetProps } from "./pwaInstallSheet"
export type { PwaInstallCardProps } from "./pwaInstallCard"
export type { BeforeInstallPromptEvent, PwaInstallEnvironment, UsePwaInstallOptions, UsePwaInstallResult } from "./usePwaInstall"
